/** Каталог кодов ошибок API (ТЗ 10 §3). Клиент ориентируется только на `code`. */
export const ERROR_HTTP = {
  VALIDATION_ERROR: 400,
  IDEMPOTENCY_KEY_REQUIRED: 400,
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  REFRESH_TOKEN_REUSED: 401,
  DEVICE_REQUIRED: 401,
  DEVICE_REVOKED: 401,
  INVALID_CREDENTIALS: 401,
  TWO_FACTOR_REQUIRED: 401,
  FEATURE_NOT_IN_PLAN: 402,
  PLAN_LIMIT_REACHED: 402,
  SUBSCRIPTION_READ_ONLY: 402,
  SUBSCRIPTION_SUSPENDED: 402,
  QUOTA_EXCEEDED: 402,
  PERMISSION_DENIED: 403,
  BRANCH_ACCESS_DENIED: 403,
  APPROVAL_REQUIRED: 403,
  APPROVAL_INVALID: 403,
  APPROVAL_USED: 403,
  DISCOUNT_INVALID: 422,
  BRANCH_INACTIVE: 409,
  NOT_FOUND: 404,
  VERSION_CONFLICT: 409,
  INVALID_STATUS_TRANSITION: 409,
  ENTITY_IN_USE: 409,
  DUPLICATE: 409,
  SLUG_TAKEN: 409,
  IDEMPOTENCY_IN_PROGRESS: 409,
  IDEMPOTENCY_KEY_REUSED: 422,
  UNIT_CONVERSION_UNDEFINED: 422,
  RECIPE_CYCLE: 422,
  RECIPE_VERSION_PUBLISHED: 409,
  MENU_ITEM_UNAVAILABLE: 422,
  MODIFIERS_INVALID: 422,
  TABLE_UNAVAILABLE: 422,
  ITEM_ALREADY_SENT: 409,
  ORDER_CLOSED: 409,
  ORDER_HAS_PAYMENTS: 409,
  NOTHING_TO_SEND: 422,
  CURSOR_EXPIRED: 410,
  BATCH_TOO_LARGE: 413,
  OPEN_ORDERS_EXIST: 409,
  TOTALS_MISMATCH: 422,
  TERMINAL_REVOKED: 403,
  SHIFT_ALREADY_OPEN: 409,
  SHIFT_CLOSED: 409,
  TABLE_OCCUPIED: 409,
  REFUND_EXCEEDS_PAID: 422,
  CREDIT_LIMIT_EXCEEDED: 422,
  DEPOSIT_REQUIRED: 422,
  DELIVERY_ADDRESS_OUT_OF_ZONE: 422,
  CUSTOMER_DEBT_OVERDUE: 422,
  CUSTOMER_BLOCKED: 422,
  CUSTOMER_PHONE_TAKEN: 409,
  CASH_SHIFT_NOT_OPEN: 409,
  CASH_INSUFFICIENT: 422,
  INSUFFICIENT_STOCK: 409,
  DOCUMENT_POSTED: 409,
  INVENTORY_COUNT_CLOSED: 409,
  PAYMENT_AMOUNT_INVALID: 422,
  CUSTOMER_REQUIRED: 422,
  CLOCK_IN_NOT_ALLOWED: 403,
  WORK_SHIFT_OPEN: 409,
  PAYROLL_APPROVED: 409,
  IMPERSONATION_READ_ONLY: 403,
  INVOICE_PAID: 409,
  DOWNGRADE_BLOCKED: 409,
  ADDON_UNAVAILABLE: 422,
  MENU_PACK_NOT_PURCHASED: 402,
  TWO_FACTOR_INVALID: 401,
  ORDER_NOT_PAID: 409,
  FILE_TOO_LARGE: 413,
  FILE_TYPE_NOT_ALLOWED: 415,
  BUSINESS_RULE: 422,
  ACCOUNT_LOCKED: 423,
  PIN_LOCKED: 423,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

export type ErrorCode = keyof typeof ERROR_HTTP;

export type FieldError = { path: string; rule: string; message: string };

export type ApiErrorBody = {
  success: false;
  error: { code: ErrorCode; message: string; details?: Record<string, unknown> & { fields?: FieldError[] }; requestId?: string };
};

export type ApiSuccess<T, M = undefined> = { success: true; data: T } & (M extends undefined ? { meta?: undefined } : { meta: M });

export type PageMeta = { page: number; pageSize: number; total: number; /** Сумма по фильтру (minor units), если список денежный */ sum?: number; /** Непрочитанные (уведомления) */ unread?: number;
  /** Сводка по фильтру (например, просроченные партии) */ [extra: string]: unknown };

/** Сообщения по умолчанию (ru). Локализация — на клиенте по `code`. */
export const ERROR_MESSAGES_RU: Partial<Record<ErrorCode, string>> = {
  VALIDATION_ERROR: "Проверьте правильность заполнения полей",
  UNAUTHENTICATED: "Требуется вход в систему",
  TOKEN_EXPIRED: "Сессия истекла",
  REFRESH_TOKEN_REUSED: "Сессия завершена из соображений безопасности. Войдите снова",
  DEVICE_REQUIRED: "Вход по PIN доступен только на зарегистрированном терминале",
  DEVICE_REVOKED: "Терминал отключён администратором",
  INVALID_CREDENTIALS: "Неверный логин или пароль",
  MENU_PACK_NOT_PURCHASED: "Готовое меню ещё не оплачено — загрузить его можно после оплаты счёта",
  FEATURE_NOT_IN_PLAN: "Функция недоступна в вашем тарифе",
  PLAN_LIMIT_REACHED: "Достигнут лимит тарифа",
  SUBSCRIPTION_READ_ONLY: "Подписка в режиме только чтения",
  SUBSCRIPTION_SUSPENDED: "Подписка приостановлена",
  PERMISSION_DENIED: "Недостаточно прав",
  BRANCH_ACCESS_DENIED: "Нет доступа к филиалу",
  NOT_FOUND: "Не найдено",
  VERSION_CONFLICT: "Данные изменились. Обновите страницу",
  DUPLICATE: "Такая запись уже существует",
  SLUG_TAKEN: "Адрес уже занят",
  ACCOUNT_LOCKED: "Аккаунт временно заблокирован",
  PIN_LOCKED: "Вход по PIN временно заблокирован",
  RATE_LIMITED: "Слишком много попыток. Попробуйте позже",
  INTERNAL_ERROR: "Внутренняя ошибка сервера",
  UNIT_CONVERSION_UNDEFINED: "Единица не переводится в единицу продукта — укажите плотность продукта",
  RECIPE_CYCLE: "Полуфабрикат не может содержать сам себя (или вложенность больше 5 уровней)",
  RECIPE_VERSION_PUBLISHED: "Опубликованную версию ТТК изменить нельзя — создайте новую",
  MENU_ITEM_UNAVAILABLE: "Позиция недоступна для продажи",
  MODIFIERS_INVALID: "Проверьте выбор модификаторов",
  TABLE_UNAVAILABLE: "Стол недоступен",
  ITEM_ALREADY_SENT: "Позиция уже отправлена на кухню — изменить нельзя, только отменить",
  ORDER_CLOSED: "Заказ закрыт",
  ORDER_HAS_PAYMENTS: "По заказу есть оплаты — отмена только через возврат",
  NOTHING_TO_SEND: "Нет новых позиций для отправки на кухню",
  CURSOR_EXPIRED: "Курсор старше журнала изменений — загрузите снимок",
  BATCH_TOO_LARGE: "Слишком большая пачка: не больше 200 мутаций и 5 МБ",
  OPEN_ORDERS_EXIST: "Есть открытые заказы — смена валюты невозможна",
  TOTALS_MISMATCH: "Суммы заказа не сходятся со строками",
  TERMINAL_REVOKED: "Касса отключена администратором",
  SHIFT_ALREADY_OPEN: "На кассе уже открыта смена",
  SHIFT_CLOSED: "Смена уже закрыта",
  TABLE_OCCUPIED: "Стол занят",
  REFUND_EXCEEDS_PAID: "Сумма возврата больше оплаченного",
  CREDIT_LIMIT_EXCEEDED: "Превышен кредитный лимит клиента",
  DEPOSIT_REQUIRED: "Банкет подтверждается после внесения депозита",
  DELIVERY_ADDRESS_OUT_OF_ZONE: "Адрес вне зон доставки",
  CUSTOMER_DEBT_OVERDUE: "У клиента просроченный долг — новый кредит запрещён",
  CUSTOMER_BLOCKED: "Клиент заблокирован",
  CUSTOMER_PHONE_TAKEN: "Клиент с таким телефоном уже есть",
  CASH_SHIFT_NOT_OPEN: "Кассовая смена не открыта",
  CASH_INSUFFICIENT: "В кассе недостаточно наличных",
  INSUFFICIENT_STOCK: "Недостаточно остатка на складе",
  APPROVAL_REQUIRED: "Нужно подтверждение менеджера",
  APPROVAL_INVALID: "Подтверждение не подходит к этому действию или истекло",
  APPROVAL_USED: "Подтверждение уже использовано",
  DISCOUNT_INVALID: "Скидка больше суммы позиций, на которые она разрешена",
  BRANCH_INACTIVE: "Филиал выключен — изменения в нём недоступны",
  DOCUMENT_POSTED: "Проведённый документ нельзя изменить или удалить",
  INVENTORY_COUNT_CLOSED: "Инвентаризация уже проведена или отменена",
  PAYMENT_AMOUNT_INVALID: "Сумма безналичной оплаты больше остатка к оплате",
  CUSTOMER_REQUIRED: "Для оплаты в долг выберите клиента",
  CLOCK_IN_NOT_ALLOWED: "Отметка смены недоступна",
  WORK_SHIFT_OPEN: "У сотрудника уже открыта смена",
  PAYROLL_APPROVED: "Ведомость утверждена — изменить нельзя",
  IMPERSONATION_READ_ONLY: "Сеанс поддержки только для чтения",
  INVOICE_PAID: "Счёт оплачен — изменить нельзя",
  DOWNGRADE_BLOCKED: "Использование превышает лимиты нового тарифа",
  ADDON_UNAVAILABLE: "Дополнение недоступно на этом тарифе",
  TWO_FACTOR_INVALID: "Неверный код подтверждения",
  ORDER_NOT_PAID: "По заказу нет оплат для возврата",
  FILE_TOO_LARGE: "Файл слишком большой (до 10 МБ)",
  FILE_TYPE_NOT_ALLOWED: "Допустимы изображения JPEG, PNG, WebP, AVIF, HEIC",
  SERVICE_UNAVAILABLE: "Сервис временно недоступен",
  INVALID_STATUS_TRANSITION: "Это действие недоступно в текущем статусе — обновите страницу",
  ENTITY_IN_USE: "Запись используется и не может быть удалена",
  BUSINESS_RULE: "Действие невозможно",
  QUOTA_EXCEEDED: "Квота исчерпана — подключите пакет в разделе «Подписка»",
  IDEMPOTENCY_KEY_REQUIRED: "Не передан ключ повтора запроса",
  IDEMPOTENCY_IN_PROGRESS: "Запрос уже выполняется — подождите",
  IDEMPOTENCY_KEY_REUSED: "Этот запрос уже был отправлен с другими данными",
};
